/**
 * Lightweight auth state. The user logs in by entering an API key, which
 * we validate against /health (which returns user/faction context) and
 * /subscriptions (admin status, available factions).
 *
 * State is exposed via a tiny store (subscribe + getSnapshot) consumed
 * by the `useAuth` hook in src/hooks. This avoids pulling in Zustand or
 * Redux for what amounts to four mutable fields.
 */
import { API_KEY_STORAGE } from "./constants";
import { apiFetch } from "./api";
import { clearAdminFactionOverride } from "./adminFaction";

export interface AuthUser {
  id: number;
  name: string;
}

export interface AuthFaction {
  id: number;
  name: string;
}

export interface AuthState {
  user: AuthUser | null;
  faction: AuthFaction | null;
  isServiceAdmin: boolean;
  isLeader: boolean;
  /** Hydrating from localStorage on app boot. */
  loading: boolean;
}

type Listener = (s: AuthState) => void;

const initial: AuthState = {
  user: null,
  faction: null,
  isServiceAdmin: false,
  isLeader: false,
  loading: true,
};

let state: AuthState = { ...initial };
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(state);
}

function setState(patch: Partial<AuthState>) {
  state = { ...state, ...patch };
  emit();
}

export function getAuthState(): AuthState {
  return state;
}

export function subscribeAuth(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

interface HealthResponse {
  user?: { id: number; name: string; faction_id?: number; faction_name?: string };
}

interface SubscriptionsResponse {
  is_admin?: boolean;
  subscriptions?: unknown[];
}

interface QueueSettingsResponse {
  is_leader?: boolean;
}

async function loadContext(): Promise<void> {
  const health = await apiFetch<HealthResponse>("/health");
  if (!health.user) {
    throw { status: 401, detail: "Invalid API key" };
  }
  const user = { id: health.user.id, name: health.user.name };
  const faction = health.user.faction_id
    ? { id: health.user.faction_id, name: health.user.faction_name ?? "" }
    : null;

  // Admin status comes from /subscriptions; leader status from queue/settings.
  // Both are best-effort — if the user lacks access to either endpoint, fall
  // back to non-admin / non-leader.
  let isServiceAdmin = false;
  try {
    const subData = await apiFetch<SubscriptionsResponse>("/subscriptions");
    isServiceAdmin = subData.is_admin === true;
  } catch {
    // ignore
  }

  let isLeader = false;
  try {
    const queueSettings = await apiFetch<QueueSettingsResponse>("/queue/settings");
    isLeader = queueSettings.is_leader === true;
  } catch {
    // ignore
  }

  setState({ user, faction, isServiceAdmin, isLeader, loading: false });
}

export async function login(apiKey: string): Promise<void> {
  localStorage.setItem(API_KEY_STORAGE, apiKey);
  setState({ loading: true });
  try {
    await loadContext();
  } catch (e) {
    localStorage.removeItem(API_KEY_STORAGE);
    setState({ ...initial, loading: false });
    throw e;
  }
}

export function logout(): void {
  localStorage.removeItem(API_KEY_STORAGE);
  // Drop any admin "view as" override; the next user shouldn't inherit it.
  clearAdminFactionOverride();
  setState({ ...initial, loading: false });
}

/** Restore session from localStorage on app boot. */
export async function tryRestoreSession(): Promise<void> {
  const stored = localStorage.getItem(API_KEY_STORAGE);
  if (!stored) {
    setState({ loading: false });
    return;
  }
  try {
    await loadContext();
  } catch {
    localStorage.removeItem(API_KEY_STORAGE);
    setState({ ...initial, loading: false });
  }
}
